import { FunctionComponent } from "react";
import { Subject } from "rxjs";

import useIsMounted from "ismounted";
import { useContext, useEffect, useMemo, useState } from "react";
import { DeepPartial, FormProvider, useForm } from "react-hook-form";
import { useAppLoader } from "../../../../../components/loader/app-loader-hook";
import { StepperContext } from "../../../../../components/stepper/context";
import { VerificationDTO } from "../../../../../store/verification/models/dto/due-diligence";
import { VerificationService } from "../../../../../store/verification/service";
import { ProofofFace } from "../common/pages/kyc-details/proof-of-face";
import { yupResolver } from "@hookform/resolvers/yup";
import { reach } from "yup";

type FormType = VerificationDTO.Requests.DueDiligence.Form.Post.Participant.T;

const SelfieStep: FunctionComponent<{
  data: DeepPartial<FormType>;
  code: string;
}> = ({ data, code }) => {
  const emailChanged = useMemo(() => new Subject<string>(), []);

  const isMounted = useIsMounted();
  const {
    validation,
  } = VerificationDTO.Requests.DueDiligence.Form.Post.Participant.useSchemas();

  const { setValidation } = useContext(StepperContext);
  const [fileUploaded, setFileUploaded] = useState(false);
  const loader = useAppLoader();
  const form = useForm<{
    selfie: any;
  }>({
    resolver: yupResolver(reach(validation, "")),
  });

  /** Set up the validation for the form */
  useEffect(() => {
    setValidation(() => () =>
      new Promise<void>((res, rej) => {
        form.handleSubmit(() => {
          if (!fileUploaded) {
            return rej(new Error("Missing attachments"));
          }

          validation
            .validate(data)
            .then(() =>
              loader(
                VerificationService.postDueDiligenceFormFinalParticipant(
                  code,
                  data as FormType
                )
              )
            )
            .then(res, rej);
        })();
      })
    );

    return () => {
      if (isMounted.current) {
        // We have to check if it's mounted otherwise it will clear current validation
        // This is because the prior page's clear validation is run after the new page's setValidation
        setValidation(() => () => Promise.resolve());
      }
    };
  }, [
    setValidation,
    isMounted.current,
    fileUploaded,
    data,
    validation,
    form.handleSubmit,
  ]);

  return (
    <FormProvider {...form}>
      <ProofofFace
        emailChange={emailChanged}
        setFilesUploaded={setFileUploaded}
      />
    </FormProvider>
  );
};

export default SelfieStep;
